import { useEffect, useState } from "react";
import { Menu, X, Phone } from "lucide-react";
import { ASSETS, SITE } from "@/lib/site";

const NAV_LINKS = [
  { id: "about", label: "About" },
  { id: "director", label: "Director" },
  { id: "zero-to-one", label: "Zero to One" },
  { id: "upcoming", label: "Upcoming" },
  { id: "portfolio", label: "Portfolio" },
  { id: "contact", label: "Contact" },
];

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goTo = (id) => {
    setOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? "glass-nav py-3" : "py-5 bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 flex items-center justify-between">
        <button
          onClick={() => goTo("hero")}
          data-testid="navbar-logo-btn"
          className="flex items-center gap-3 group"
        >
          <div className="w-11 h-11 rounded-full bg-white/95 p-1 ring-1 ring-white/20 group-hover:ring-[#2DD4BF]/60 transition-all">
            <img src={ASSETS.logo} alt="AU Movies" className="w-full h-full object-contain rounded-full" />
          </div>
          <div className="flex flex-col leading-tight">
            <span className="font-serif text-lg tracking-wide text-white">{SITE.name}</span>
            <span className="text-[10px] uppercase tracking-[0.2em] text-[#2DD4BF]">Production House</span>
          </div>
        </button>

        <nav className="hidden lg:flex items-center gap-8">
          {NAV_LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-link-${l.id}`}
              onClick={() => goTo(l.id)}
              className="text-sm tracking-wide text-slate-300 hover:text-[#2DD4BF] transition-colors"
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-4">
          <a
            href={`tel:${SITE.phone}`}
            data-testid="navbar-call-btn"
            className="btn-ghost text-sm"
          >
            <Phone size={14} /> {SITE.phoneDisplay}
          </a>
        </div>

        <button
          className="lg:hidden text-white p-2"
          onClick={() => setOpen((v) => !v)}
          data-testid="navbar-menu-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden glass-nav border-t border-white/5">
          <div className="max-w-7xl mx-auto px-6 py-4 flex flex-col gap-3">
            {NAV_LINKS.map((l) => (
              <button
                key={l.id}
                data-testid={`nav-link-mobile-${l.id}`}
                onClick={() => goTo(l.id)}
                className="text-left text-slate-200 py-2 border-b border-white/5 hover:text-[#2DD4BF]"
              >
                {l.label}
              </button>
            ))}
            <a
              href={`tel:${SITE.phone}`}
              className="btn-aum mt-2 justify-center"
              data-testid="navbar-call-btn-mobile"
            >
              <Phone size={14} /> Call Us
            </a>
          </div>
        </div>
      )}
    </header>
  );
};
